import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { MapPin, Navigation, Loader2, Car, Clock, RotateCcw, Download, ArrowRight, RotateCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const DEFAULT_FROM_ADDRESS = "1 S Center St, Reno, NV 89501";

const DistanceCalculator = ({ open, onClose, onImport }) => {
  const [fromAddress, setFromAddress] = useState(DEFAULT_FROM_ADDRESS);
  const [toAddress, setToAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculateDistance = async () => {
    if (!fromAddress.trim() || !toAddress.trim()) {
      toast.error('Please enter both From and To addresses');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(`${API}/admin/calculate-distance`, {
        from_address: fromAddress.trim(),
        to_address: toAddress.trim()
      });

      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.message || 'Unable to calculate distance');
      }
    } catch (err) {
      console.error('Distance calculation error:', err);
      setError(err.response?.data?.detail || 'Failed to calculate distance. Please check the addresses and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFromAddress(DEFAULT_FROM_ADDRESS);
    setToAddress('');
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleImportOneWay = () => {
    if (result && onImport) {
      onImport(Math.round(result.one_way_miles));
      toast.success(`Imported ${Math.round(result.one_way_miles)} miles (one way)`);
      handleClose();
    }
  };

  const handleImportRoundTrip = () => {
    if (result && onImport) {
      onImport(Math.round(result.round_trip_miles));
      toast.success(`Imported ${Math.round(result.round_trip_miles)} miles (round trip)`);
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 sm:max-w-md" data-testid="distance-calculator-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-100 flex items-center gap-2">
            <Navigation className="h-5 w-5 text-teal-400" />
            Distance Calculator
          </DialogTitle>
          <DialogDescription className="text-white">
            Calculate driving distance and travel time between two locations.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* From Address */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <MapPin className="h-4 w-4 text-emerald-400" />
              From
            </Label>
            <Input
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              placeholder="Enter starting address"
              className="bg-slate-900 border-teal-600 text-slate-100 placeholder:text-slate-500"
              data-testid="distance-from-input"
            />
          </div>

          {/* To Address */}
          <div className="space-y-2">
            <Label className="text-white flex items-center gap-2">
              <MapPin className="h-4 w-4 text-red-400" />
              To
            </Label>
            <Input
              value={toAddress}
              onChange={(e) => setToAddress(e.target.value)}
              placeholder="Enter destination address"
              className="bg-slate-900 border-teal-600 text-slate-100 placeholder:text-slate-500"
              data-testid="distance-to-input"
              onKeyDown={(e) => e.key === 'Enter' && calculateDistance()}
            />
          </div>

          {/* Calculate Button */}
          <div className="flex gap-2">
            <Button
              onClick={calculateDistance}
              disabled={loading || !fromAddress.trim() || !toAddress.trim()}
              className="flex-1 bg-teal-600 hover:bg-teal-500 text-white"
              data-testid="calculate-distance-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <Car className="h-4 w-4 mr-2" />
                  Calculate Distance
                </>
              )}
            </Button>
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-teal-600 text-white hover:bg-slate-700"
              data-testid="reset-distance-btn"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>

          {/* Error Display */}
          {error && (
            <Card className="bg-red-900/30 border-red-700 p-4">
              <p className="text-red-300 text-sm">{error}</p>
            </Card>
          )}

          {/* Results Display */}
          {result && (
            <Card className="bg-slate-900 border-slate-700 p-4 space-y-3" data-testid="distance-result">
              <div className="text-center">
                <p className="text-white text-sm mb-1">Driving Distance</p>
                <div className="flex items-center justify-center gap-4">
                  <div>
                    <p className="text-3xl font-bold text-teal-400">{result.one_way_miles}</p>
                    <p className="text-xs text-slate-500">miles (one way)</p>
                  </div>
                  <div className="text-slate-600">|</div>
                  <div>
                    <p className="text-3xl font-bold text-emerald-400">{result.round_trip_miles}</p>
                    <p className="text-xs text-slate-500">miles (round trip)</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-slate-700 pt-3">
                <div className="flex items-center justify-center gap-2 text-white">
                  <Clock className="h-4 w-4 text-amber-400" />
                  <span>Estimated Travel Time:</span>
                  <span className="font-semibold text-amber-400">{result.travel_time_minutes} min</span>
                  <span className="text-slate-500">(one way)</span>
                </div>
              </div>

              {/* Import Buttons - Only show if onImport callback is provided */}
              {onImport && (
                <div className="border-t border-slate-700 pt-3">
                  <p className="text-xs text-white mb-2 text-center">Import miles to mileage field:</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleImportOneWay}
                      className="flex-1 bg-teal-600 hover:bg-teal-500 text-white text-sm"
                      data-testid="import-one-way-btn"
                    >
                      <ArrowRight className="h-3.5 w-3.5 mr-1" />
                      One Way ({Math.round(result.one_way_miles)} mi)
                    </Button>
                    <Button
                      onClick={handleImportRoundTrip}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
                      data-testid="import-round-trip-btn"
                    >
                      <RotateCw className="h-3.5 w-3.5 mr-1" />
                      Round Trip ({Math.round(result.round_trip_miles)} mi)
                    </Button>
                  </div>
                </div>
              )}

              {/* Address Summary */}
              <div className="border-t border-slate-700 pt-3 text-xs text-slate-500 space-y-1">
                <div className="flex items-start gap-2">
                  <span className="text-emerald-400">From:</span>
                  <span className="flex-1">{result.from_address}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-red-400">To:</span>
                  <span className="flex-1">{result.to_address}</span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DistanceCalculator;
